/**
 * Renderização do texto da mensagem.
 *
 * Escrito à mão em vez de usar uma biblioteca de markdown por dois motivos: o
 * subconjunto que interessa num chat é pequeno, e o resultado sai como
 * elementos React, nunca como HTML injetado. Sem `dangerouslySetInnerHTML` não
 * existe caminho de XSS por mensagem, que é o vetor mais óbvio numa plataforma
 * onde qualquer um escreve para todos.
 *
 * Suportado: **negrito**, *itálico*, __sublinhado__, ~~riscado~~, `código`,
 * ```blocos```, > citação, links, menções e quebras de linha.
 */

import { Fragment, type ReactNode } from 'react'
import { useMember } from '@/hooks/selectors'
import { cn } from '@/lib/cn'

const CODE_BLOCK_RE = /```(?:([a-zA-Z0-9+#-]*)\n)?([\s\S]*?)```/g

export function MessageContent({
  content,
  guildId,
}: {
  content: string
  guildId: string | null
}) {
  if (!content) return null

  // Blocos de código saem primeiro: dentro deles nada mais é interpretado.
  const segments: ReactNode[] = []
  let lastIndex = 0
  let match: RegExpExecArray | null
  let key = 0

  CODE_BLOCK_RE.lastIndex = 0
  while ((match = CODE_BLOCK_RE.exec(content)) !== null) {
    if (match.index > lastIndex) {
      segments.push(
        <Fragment key={key++}>
          {renderLines(content.slice(lastIndex, match.index), guildId, key)}
        </Fragment>,
      )
    }

    const language = match[1] ?? ''
    const code = (match[2] ?? '').replace(/\n$/, '')

    segments.push(
      <pre
        key={key++}
        className="my-1 overflow-x-auto rounded-md bg-surface-deepest p-3 text-[13px] leading-relaxed"
      >
        {language ? (
          <div className="mb-1.5 text-[10px] font-semibold tracking-wide text-content-muted uppercase">
            {language}
          </div>
        ) : null}
        <code className="font-mono text-content-primary">{code}</code>
      </pre>,
    )

    lastIndex = match.index + match[0].length
  }

  if (lastIndex < content.length) {
    segments.push(
      <Fragment key={key++}>{renderLines(content.slice(lastIndex), guildId, key)}</Fragment>,
    )
  }

  return (
    <div className="text-[15px] leading-[1.4] break-words whitespace-pre-wrap text-content-primary">
      {segments}
    </div>
  )
}

function renderLines(text: string, guildId: string | null, baseKey: number): ReactNode {
  const lines = text.split('\n')

  return lines.map((line, index) => {
    const isQuote = line.startsWith('> ')
    const body = isQuote ? line.slice(2) : line

    const rendered = renderInline(body, guildId, `${baseKey}-${index}`)

    if (isQuote) {
      return (
        <div key={index} className="my-0.5 border-l-4 border-line-strong py-0.5 pl-3">
          <span className="text-content-secondary">{rendered}</span>
        </div>
      )
    }

    return (
      <Fragment key={index}>
        {rendered}
        {index < lines.length - 1 ? '\n' : null}
      </Fragment>
    )
  })
}

/**
 * Aplica a formatação inline.
 *
 * A ordem dos padrões importa: `**` precisa vir antes de `*`, senão o itálico
 * come metade do negrito.
 */
const INLINE_PATTERNS: {
  regex: RegExp
  render: (text: string, key: string) => ReactNode
}[] = [
  {
    regex: /`([^`\n]+)`/,
    render: (text, key) => (
      <code
        key={key}
        className="rounded bg-surface-deepest px-1.5 py-0.5 font-mono text-[13px] text-content-primary"
      >
        {text}
      </code>
    ),
  },
  {
    regex: /\*\*([^*]+)\*\*/,
    render: (text, key) => (
      <strong key={key} className="font-bold">
        {text}
      </strong>
    ),
  },
  {
    regex: /__([^_]+)__/,
    render: (text, key) => (
      <u key={key} className="underline">
        {text}
      </u>
    ),
  },
  {
    regex: /~~([^~]+)~~/,
    render: (text, key) => (
      <s key={key} className="line-through opacity-70">
        {text}
      </s>
    ),
  },
  {
    regex: /\*([^*\n]+)\*/,
    render: (text, key) => (
      <em key={key} className="italic">
        {text}
      </em>
    ),
  },
]

const URL_RE = /https?:\/\/[^\s<>"']+/
const MENTION_RE = /<@(\d+)>/

function renderInline(text: string, guildId: string | null, keyPrefix: string): ReactNode[] {
  if (!text) return []

  // Menções e links vêm antes da formatação: uma URL com underline no meio não
  // deve virar texto sublinhado.
  const mentionMatch = MENTION_RE.exec(text)
  if (mentionMatch) {
    const before = text.slice(0, mentionMatch.index)
    const after = text.slice(mentionMatch.index + mentionMatch[0].length)
    return [
      ...renderInline(before, guildId, `${keyPrefix}-b`),
      <Mention key={`${keyPrefix}-m`} userId={mentionMatch[1] as string} guildId={guildId} />,
      ...renderInline(after, guildId, `${keyPrefix}-a`),
    ]
  }

  const urlMatch = URL_RE.exec(text)
  if (urlMatch) {
    const before = text.slice(0, urlMatch.index)
    const url = urlMatch[0]
    const after = text.slice(urlMatch.index + url.length)
    return [
      ...renderInline(before, guildId, `${keyPrefix}-b`),
      <a
        key={`${keyPrefix}-u`}
        href={url}
        target="_blank"
        // noopener impede a página de destino de manipular esta aba pelo
        // window.opener; noreferrer evita vazar de onde o clique veio.
        rel="noopener noreferrer"
        className="text-brand hover:underline"
      >
        {url}
      </a>,
      ...renderInline(after, guildId, `${keyPrefix}-a`),
    ]
  }

  for (const [patternIndex, pattern] of INLINE_PATTERNS.entries()) {
    const match = pattern.regex.exec(text)
    if (!match) continue

    const before = text.slice(0, match.index)
    const after = text.slice(match.index + match[0].length)

    return [
      ...renderInline(before, guildId, `${keyPrefix}-b${patternIndex}`),
      pattern.render(match[1] as string, `${keyPrefix}-p${patternIndex}`),
      ...renderInline(after, guildId, `${keyPrefix}-a${patternIndex}`),
    ]
  }

  return [text]
}

function Mention({ userId, guildId }: { userId: string; guildId: string | null }) {
  const member = useMember(guildId, userId)
  const name = member?.nickname ?? member?.user.displayName ?? 'usuário'

  return (
    <span
      className={cn(
        'cursor-pointer rounded px-1 py-px font-medium',
        'bg-brand-subtle text-brand transition-colors hover:bg-brand hover:text-white',
      )}
    >
      @{name}
    </span>
  )
}
